import { FC, useMemo, useState } from "react";

import {
  Button,
  ButtonText,
  HStack,
  Input,
  InputField,
  Pressable,
  Text,
  Textarea,
  TextareaInput,
  VStack,
} from "@/components/ui";

import { formatMargin, formatPercent } from "./colors";
import { FieldLabel, Segmented } from "./Controls";
import { parseExternal } from "./external";
import { STATES } from "./geo";
import Sheet from "./Sheet";
import type { ExternalMetric, ExternalSource, Office, Race } from "./types";
import { OFFICE_LABEL } from "./view";

interface SourcesSheetProps {
  activeId: string | null;
  defaultOffice: Office;
  onActivate: (id: string | null) => void;
  onClose: () => void;
  onDelete: (id: string) => void;
  onSave: (source: ExternalSource) => void;
  open: boolean;
  races: Race[];
  sources: ExternalSource[];
}

const METRICS: { label: string; value: ExternalMetric }[] = [
  { label: "Auto-detect", value: "auto" },
  { label: "Win probability", value: "probability" },
  { label: "Vote shares", value: "shares" },
  { label: "Margin (D+3)", value: "margin" },
  { label: "Ratings (Lean R)", value: "rating" },
];

const OFFICES: { label: string; value: Office }[] = [
  { label: "Senate", value: "senate" },
  { label: "Governor", value: "governor" },
  { label: "House", value: "house" },
];

const PREVIEW_ROWS = 12;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
  });

/** "35 Senate · 36 Governor · 435 House" (the national row aside). */
const raceCounts = (source: ExternalSource) =>
  OFFICES.flatMap(({ label, value }) => {
    const count = source.rows.filter(
      (row) => row.office === value && row.state !== "US"
    ).length;
    return count > 0 ? [`${count} ${label}`] : [];
  }).join(" · ");

/** Paste a forecaster's table, preview what was recognized, save it. */
const SourcesSheet: FC<SourcesSheetProps> = ({
  activeId,
  defaultOffice,
  onActivate,
  onClose,
  onDelete,
  onSave,
  open,
  races,
  sources,
}) => {
  const [label, setLabel] = useState("");
  const [metric, setMetric] = useState<ExternalMetric>("auto");
  const [office, setOffice] = useState<Office>(defaultOffice);
  const [text, setText] = useState("");

  // Follow the tracker's office during render (not in an effect) when it
  // changes: the sheet stays mounted while closed.
  const [prevDefaultOffice, setPrevDefaultOffice] = useState(defaultOffice);
  if (prevDefaultOffice !== defaultOffice) {
    setPrevDefaultOffice(defaultOffice);
    setOffice(defaultOffice);
  }

  const parsed = useMemo(
    () =>
      text.trim()
        ? parseExternal(text, {
            defaultOffice: office,
            metric,
            races,
            states: STATES,
          })
        : null,
    [metric, office, races, text]
  );

  const save = () => {
    if (!parsed || parsed.rows.length === 0) return;
    const importedAt = new Date().toISOString();
    onSave({
      id: `${Date.now()}`,
      importedAt,
      label: label.trim() || "External",
      rows: parsed.rows,
    });
    setText("");
  };

  return (
    <Sheet
      onClose={onClose}
      open={open}
      subtitle="Compare Foresight with published forecasts"
      title="External forecasts"
    >
      {sources.length > 0 && (
        <VStack className="gap-2">
          <FieldLabel label="Saved sources" />
          {sources.map((source) => {
            const active = source.id === activeId;
            return (
              <HStack
                className={`items-center gap-3 rounded-xl border px-4 py-3 ${active ? "border-primary-500 bg-primary-50" : "border-outline-200"}`}
                key={source.id}
              >
                <VStack className="flex-1">
                  <Text className="text-sm font-semibold text-typography-900">
                    {source.label}
                  </Text>
                  <Text className="text-xs text-typography-500">
                    {[
                      raceCounts(source),
                      `${source.builtIn ? "built in, as of" : "pasted"} ${formatDate(source.importedAt)}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                </VStack>
                <Pressable
                  onPress={() => onActivate(active ? null : source.id)}
                >
                  <Text className="text-xs font-medium text-primary-600">
                    {active ? "Hide" : "Compare"}
                  </Text>
                </Pressable>
                {!source.builtIn && (
                  <Pressable onPress={() => onDelete(source.id)}>
                    <Text className="text-xs font-medium text-error-600">
                      Delete
                    </Text>
                  </Pressable>
                )}
              </HStack>
            );
          })}
        </VStack>
      )}

      <VStack className="gap-4">
        <FieldLabel label="New source" />
        <Text className="text-sm leading-5 text-typography-600">
          Copy a forecast table (Silver Bulletin, Strength In Numbers, Cook, a
          prediction market…) and paste it below, one race per line. Each line
          needs a state name or postal code; candidate names or party markers
          (D, R, Dem, GOP) tell which number is whose. A line with “National” or
          “Generic ballot” sets the House national figure. Saving under an
          existing name adds to that source, so Senate and governor tables can
          be pasted one after the other.
        </Text>
        <VStack className="gap-1.5">
          <Text className="text-sm font-medium text-typography-800">Name</Text>
          <Input className="border border-outline-200 rounded-xl bg-background-50">
            <InputField
              onChangeText={setLabel}
              placeholder="e.g. Cook Political Report"
              value={label}
            />
          </Input>
        </VStack>
        <VStack className="gap-1.5">
          <Text className="text-sm font-medium text-typography-800">
            The numbers are
          </Text>
          <Segmented onChange={setMetric} options={METRICS} value={metric} />
        </VStack>
        <VStack className="gap-1.5">
          <Text className="text-sm font-medium text-typography-800">
            Office when a line doesn’t say
          </Text>
          <Segmented onChange={setOffice} options={OFFICES} value={office} />
        </VStack>
        <Textarea className="h-48 border border-outline-200 rounded-xl bg-background-50">
          <TextareaInput
            multiline
            onChangeText={setText}
            placeholder={
              "Georgia  Ossoff 71%  Collins 29%\nMaine  Senate  D+2.4\nNorth Carolina  Cooper (D) 68\nNational generic ballot D+3"
            }
            value={text}
          />
        </Textarea>
        <Text className="text-xs text-typography-400">
          Internal comparison only: don’t republish paywalled numbers.
        </Text>
      </VStack>

      {parsed && (
        <VStack className="gap-2">
          <FieldLabel label="Preview" />
          <Text className="text-sm text-typography-700">
            {parsed.rows.length} race{parsed.rows.length === 1 ? "" : "s"}{" "}
            recognized
            {parsed.skipped.length > 0
              ? ` · ${parsed.skipped.length} line(s) skipped`
              : ""}
            {parsed.assumed > 0
              ? ` · ${parsed.assumed} line(s) with no name or party marker: first number read as the Democrat's`
              : ""}
          </Text>
          {parsed.rows.slice(0, PREVIEW_ROWS).map((row) => (
            <HStack
              className="items-center gap-3"
              key={`${row.office}-${row.state}`}
            >
              <Text className="w-28 text-sm text-typography-800">
                {row.state === "US"
                  ? "National"
                  : (STATES.find((s) => s.abbr === row.state)?.name ??
                    row.state)}
              </Text>
              <Text className="w-20 text-xs text-typography-500">
                {OFFICE_LABEL[row.office]}
              </Text>
              <Text className="flex-1 text-sm text-typography-900">
                {row.leftProbability !== null
                  ? `${formatPercent(row.leftProbability)} left-side win`
                  : ""}
                {row.leftProbability !== null && row.margin !== null
                  ? " · "
                  : ""}
                {row.margin !== null ? formatMargin(row.margin) : ""}
              </Text>
            </HStack>
          ))}
          {parsed.rows.length > PREVIEW_ROWS && (
            <Text className="text-xs text-typography-400">
              +{parsed.rows.length - PREVIEW_ROWS} more
            </Text>
          )}
          {parsed.skipped.slice(0, 3).map((line) => (
            <Text
              className="text-xs text-error-600"
              key={line}
              numberOfLines={1}
            >
              Skipped: {line}
            </Text>
          ))}
          <Button
            className="self-start rounded-xl mt-2"
            isDisabled={parsed.rows.length === 0}
            onPress={save}
            size="sm"
          >
            <ButtonText>Save source</ButtonText>
          </Button>
        </VStack>
      )}
    </Sheet>
  );
};

export default SourcesSheet;
